import { generateActivityMapColors } from '@/modules/activities'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import type { VehicleRoute } from './types'

interface DrivenCoordinatesInput {
  vehicleVin?: string | null
  vehicleRegistrationNumber?: string | null
  coordinates: readonly { lat: number; lng: number; timestamp: string }[]
}

interface ActivityVehicleInput {
  vehicleVin?: string | null
  vehicleRegistrationNumber?: string | null
}

export interface UsedVehicle {
  label: string
  vehicleVin: string | null
  color?: string
}

/**
 * Turns a work period's `drivenCoordinates` into per-vehicle routes, giving each
 * vehicle a stable colour and the registration number the company knows it by.
 */
export const useWorkPeriodRoute = (
  drivenCoordinates: readonly DrivenCoordinatesInput[] | undefined,
  activities: readonly ActivityVehicleInput[] | undefined
) => {
  const translations = useTranslate({
    unknownVehicle: 'common.unknown-vehicle',
  })

  const vehicleLocations = useMemo(
    () => drivenCoordinates ?? [],
    [drivenCoordinates]
  )

  const vehicleColors = useMemo<Record<string, string>>(() => {
    const colors: Record<string, string> = {}
    const vins = vehicleLocations
      .map(location => location.vehicleVin ?? 'Unknown')
      .filter((value, index, all) => all.indexOf(value) === index)
    const palette = generateActivityMapColors(vins.length)

    vins.forEach((vin, index) => {
      colors[vin] = palette[index] ?? '#0088FF'
    })

    return colors
  }, [vehicleLocations])

  // Locations identify a vehicle by VIN only, but the backend resolves the plate
  // from the company vehicle list, so a badge can show the registration number
  // the company knows the vehicle by instead of a raw VIN.
  const registrationNumberByVin = useMemo(() => {
    const registrationNumbers: Record<string, string> = {}

    vehicleLocations.forEach(location => {
      const vin = location.vehicleVin?.trim()
      const registrationNumber = location.vehicleRegistrationNumber?.trim()

      if (vin && registrationNumber)
        registrationNumbers[vin] = registrationNumber
    })

    return registrationNumbers
  }, [vehicleLocations])

  const labelForVin = useMemo(
    () => (vin: string | null) =>
      (vin ? registrationNumberByVin[vin] : null) ??
      vin ??
      translations.unknownVehicle,
    [registrationNumberByVin, translations.unknownVehicle]
  )

  const routes = useMemo<VehicleRoute[]>(
    () =>
      vehicleLocations
        .filter(location => location.coordinates.length > 0)
        .map(location => ({
          vehicleVin: location.vehicleVin ?? null,
          label: labelForVin(location.vehicleVin ?? null),
          color: vehicleColors[location.vehicleVin ?? 'Unknown'] ?? '#0088FF',
          samples: location.coordinates.map(coordinate => ({
            lat: coordinate.lat,
            lng: coordinate.lng,
            timestamp: coordinate.timestamp,
          })),
        })),
    [labelForVin, vehicleColors, vehicleLocations]
  )

  const usedVehicles = useMemo<UsedVehicle[]>(() => {
    const vehicles: UsedVehicle[] = []
    const seen = new Set<string>()

    const addVehicle = (label?: string | null, vehicleVin?: string | null) => {
      const vin = vehicleVin?.trim() || null
      const normalizedLabel = label?.trim() || labelForVin(vin)

      if (seen.has(normalizedLabel)) return

      vehicles.push({
        label: normalizedLabel,
        vehicleVin: vin,
        color: vin ? vehicleColors[vin] : undefined,
      })
      seen.add(normalizedLabel)
    }

    activities?.forEach(activity => {
      addVehicle(activity.vehicleRegistrationNumber, activity.vehicleVin)
    })

    if (vehicles.length === 0)
      vehicleLocations.forEach(location => {
        addVehicle(null, location.vehicleVin)
      })

    return vehicles
  }, [activities, labelForVin, vehicleColors, vehicleLocations])

  return {
    routes,
    usedVehicles,
    hasCoordinates: routes.length > 0,
  }
}
